<template>
  <div class="demo-route-root">
    <vs-alert v-if="content.demoLayout !== 'chat'" color="warning" active="true" class="mb-4 demo-panel-alert">
      <div class="demo-panel-alert__body">
        <span class="font-semibold">{{ $t('demoExplorerBannerTitle') }}</span>
        — {{ $t('demoExplorerBannerLead') }}
        <span class="demo-panel-alert__links">
          <router-link to="/pages/register" class="ml-2 underline font-medium">{{ $t('demoExplorerCreateAccount') }}</router-link>
        </span>
      </div>
    </vs-alert>

    <!-- Layout tipo Chat (WhatsApp, Instagram, etc.) — inspirado em views/apps/mobile/Chat.vue -->
    <div
      v-if="content.demoLayout === 'chat'"
      id="chat-app"
      class="border border-solid d-theme-border-grey-light rounded relative"
    >
      <vs-alert color="warning" active="true" class="m-0 rounded-none demo-panel-alert demo-panel-alert--chat">
        <span class="demo-panel-alert__body text-sm">{{ $t('demoExplorerDemoPrefix') }} · {{ content.chatHeadline }}</span>
      </vs-alert>
      <div class="flex flex-wrap demo-chat-layout">
        <div
          class="demo-chat-sidebar w-full md:w-1/3 lg:w-1/4 border border-solid d-theme-border-grey-light border-t-0 border-b-0 border-l-0 bg-white"
        >
          <div class="chat__profile-search flex p-4">
            <vs-input
              icon="icon-search"
              icon-pack="feather"
              class="w-full input-rounded-full no-icon-border"
              :placeholder="$t('demoExplorerSearchPlaceholder')"
              readonly
            />
          </div>
          <vs-divider class="d-theme-border-grey-light m-0" />
          <div class="p-2 demo-chat-contacts">
            <ul class="bordered-items list-none p-0 m-0">
              <li
                v-for="c in content.chatContacts"
                :key="c.id"
                class="p-3 cursor-pointer border-b border-grey-light"
                :class="{ 'bg-grey-light': c.id === activeChatId }"
                @click="activeChatId = c.id"
              >
                <p class="font-medium text-sm mb-1 truncate">{{ c.name }}</p>
                <p class="text-xs opacity-70 truncate mb-1">{{ c.preview }}</p>
                <div class="flex justify-between text-xs">
                  <span>{{ c.time }}</span>
                  <vs-chip color="warning" v-if="c.unseen">{{ c.unseen }}</vs-chip>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div
          class="demo-chat-panel w-full md:w-2/3 lg:w-3/4 chat-content-area border border-solid d-theme-border-grey-light border-t-0 border-r-0 border-b-0 d-theme-dark-bg"
        >
          <div class="chat__navbar p-4 border-b border-grey-light flex items-center flex-shrink-0">
            <feather-icon icon="MessageSquareIcon" class="mr-2" svgClasses="w-5 h-5" />
            <span class="font-medium">{{ activeChatTitle }}</span>
          </div>
          <div class="p-4 demo-chat-messages">
            <div
              v-for="(m, idx) in activeChatMessages"
              :key="idx"
              class="mb-3 flex"
              :class="m.from === 'me' ? 'justify-end' : 'justify-start'"
            >
              <div
                class="rounded-lg px-3 py-2 max-w-lg text-sm"
                :class="
                  m.from === 'me'
                    ? 'bg-primary text-white'
                    : m.from === 'system'
                    ? 'bg-grey-light text-dark'
                    : 'bg-white border border-solid border-grey-light'
                "
              >
                <span>{{ m.text }}</span>
                <div class="text-xs opacity-70 mt-1">{{ m.time }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="content.socialProofStrip && content.socialProofStrip.length" class="border-t border-solid border-grey-light">
        <p class="text-xs opacity-65 px-4 pt-3 mb-2 font-semibold">{{ $t('demoExplorerSocialProofTitle') }}</p>
        <div class="vx-row px-2 pb-2">
          <div
            v-for="(s, si) in content.socialProofStrip"
            :key="'sp-chat-' + si"
            class="vx-col w-full sm:w-1/3 mb-2"
          >
            <vx-card class="overflow-hidden">
              <div class="p-4 text-center sm:text-left">
                <p class="text-xl font-bold text-primary mb-1">{{ s.stat }}</p>
                <p class="text-xs opacity-65 mb-0 leading-relaxed">{{ s.label }}</p>
              </div>
            </vx-card>
          </div>
        </div>
      </div>

      <div class="vx-row px-2 pb-2" v-if="content.kpis && content.kpis.length">
        <div v-for="(k, i) in content.kpis" :key="'kpi-chat-' + i" class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-2">
          <vx-card>
            <div class="p-3">
              <p class="text-xs opacity-70 mb-1">{{ k.label }}</p>
              <h4 class="text-lg font-bold mb-1">{{ k.value }}</h4>
              <p v-if="k.hint" class="text-xs opacity-55 mb-0">{{ k.hint }}</p>
            </div>
          </vx-card>
        </div>
      </div>

      <div class="vx-row px-2 pb-2" v-if="content.insights && content.insights.length">
        <div v-for="(ins, j) in content.insights" :key="'ins-chat-' + j" class="vx-col w-full md:w-1/2 mb-2">
          <vx-card>
            <div class="p-3">
              <h5 class="text-sm font-semibold mb-2">{{ ins.title }}</h5>
              <p class="text-xs leading-relaxed opacity-85 mb-0">{{ ins.text }}</p>
            </div>
          </vx-card>
        </div>
      </div>

      <vx-card v-if="content.timeline && content.timeline.length" class="mx-2 mb-2 overflow-hidden">
        <div class="p-3">
          <p class="text-xs font-semibold mb-2">{{ $t('demoExplorerTimelineTitle') }}</p>
          <ul class="list-none p-0 m-0">
            <li v-for="(ev, ti) in content.timeline" :key="'tl-chat-' + ti" class="mb-2 text-xs">
              <span class="font-mono opacity-65 mr-2">{{ ev.at }}</span>
              <span>{{ ev.text }}</span>
            </li>
          </ul>
        </div>
      </vx-card>

      <p v-if="content.footnote" class="text-xs opacity-70 p-3 mb-0 border-t border-grey-light">{{ content.footnote }}</p>
    </div>

    <!-- Layout tipo lista (Site.vue / data-list-list-view) -->
    <div v-else id="data-list-list-view" class="data-list-container">
      <vx-card class="mb-4 p-4">
        <h3 class="mb-1">{{ content.title }}</h3>
        <p class="text-sm opacity-75 mb-0">{{ content.subtitle }}</p>
      </vx-card>

      <vs-table
        v-if="content.mainTable"
        :data="content.mainTable.rows"
        :noDataText="$t('demoExplorerNoData')"
      >
        <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between mb-4">
          <vs-row>
            <vs-col vs-type="flex" vs-justify="flex-start" vs-align="center" vs-w="6" vs-xs="12" class="p-0">
              <div
                class="p-4 shadow-drop rounded-lg d-theme-dark-bg flex items-center justify-start text-lg font-medium w-64 cursor-default"
              >
                <feather-icon icon="CalendarIcon" svgClasses="h-4" />
                <span class="mr-2 ml-2">{{ $t('demoExplorerLast7Days') }}</span>
              </div>
            </vs-col>
            <vs-col vs-type="flex" vs-justify="right" vs-align="center" vs-w="6" vs-xs="12" class="p-0">
              <small class="opacity-70">{{ $t('demoExplorerPageLine') }}</small>
            </vs-col>
          </vs-row>
        </div>

        <template slot="thead">
          <vs-th v-for="col in content.mainTable.columns" :key="col.field" sort-key="x">{{ col.title }}</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr :data="tr" :key="idx" v-for="(tr, idx) in data">
            <vs-td v-for="col in content.mainTable.columns" :key="col.field + '-' + idx" class="text-nowrap">
              <span>{{ data[idx][col.field] }}</span>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <div class="vx-row mt-4" v-if="content.kpis && content.kpis.length">
        <div v-for="(k, i) in content.kpis" :key="'kpi-' + i" class="vx-col w-full sm:w-1/2 lg:w-1/4 mb-4">
          <vx-card>
            <div class="p-4">
              <p class="text-xs opacity-70 mb-1">{{ k.label }}</p>
              <h4 class="text-lg font-bold mb-1">{{ k.value }}</h4>
              <p v-if="k.hint" class="text-xs opacity-60 mb-0">{{ k.hint }}</p>
            </div>
          </vx-card>
        </div>
      </div>

      <div class="vx-row mt-4" v-if="content.insights && content.insights.length">
        <div v-for="(ins, j) in content.insights" :key="'ins-' + j" class="vx-col w-full md:w-1/2 mb-4">
          <vx-card>
            <div class="p-4">
              <h4 class="mb-2">{{ ins.title }}</h4>
              <p class="text-sm leading-relaxed mb-0">{{ ins.text }}</p>
            </div>
          </vx-card>
        </div>
      </div>

      <vx-card v-if="content.timeline && content.timeline.length" class="mt-4">
        <div class="p-4">
          <h4 class="mb-3">{{ $t('demoExplorerTimelineSubtitle') }}</h4>
          <ul class="list-none p-0 m-0">
            <li v-for="(ev, ti) in content.timeline" :key="'tl-' + ti" class="mb-2 text-sm">
              <span class="font-mono text-xs opacity-70 mr-2">{{ ev.at }}</span>
              <span>{{ ev.text }}</span>
            </li>
          </ul>
        </div>
      </vx-card>

      <p v-if="content.footnote" class="text-xs opacity-70 mt-4">{{ content.footnote }}</p>
    </div>
  </div>
</template>

<script>
import { getDemoScreen } from '@/demo/richContent'

export default {
  name: 'DemoExplorer',
  data() {
    return {
      activeChatId: 1
    }
  },
  computed: {
    demoView() {
      return this.$route.meta.demoView
    },
    content() {
      return getDemoScreen(this.demoView, this.$i18n.locale)
    },
    activeChatTitle() {
      if (!this.content.chatContacts) return ''
      const c = this.content.chatContacts.find((x) => x.id === this.activeChatId)
      return c ? c.name : ''
    },
    activeChatMessages() {
      const t = this.content.chatThreads
      if (t && t[this.activeChatId] && t[this.activeChatId].length) {
        return t[this.activeChatId]
      }
      return this.content.chatMessages || []
    }
  },
  watch: {
    content: {
      handler() {
        if (this.content.chatContacts && this.content.chatContacts.length) {
          this.activeChatId = this.content.chatContacts[0].id
        }
      },
      immediate: true
    },
    $route() {
      if (this.content.chatContacts && this.content.chatContacts.length) {
        this.activeChatId = this.content.chatContacts[0].id
      }
    }
  }
}
</script>
